import React, { useState } from 'react';
import { Plus, Loader2, MapPin, Building2, Calendar, Cable, Wrench, Settings, Activity, Trash2, Package } from 'lucide-react';
import type { Project } from '../../../project/types/project';
import { useProjectPagination } from '../../../project/hooks/useProjectPagination';
import { STATUS_COLORS, STATUS_TYPES } from '../../constants/filterConstants';
import { Button } from '../../../../components/ui/button';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { Checkbox } from '../../../../components/ui/checkbox';
import { useDispatch } from 'react-redux';
//import { bulkDeleteProjects } from '../../../../redux/Project/action';
import { ProjectDeleteModal } from './ProjectDeleteModal';
import { useUserPermissionsAfterLogin } from '../../../admin/hooks/useUserPermissionsAfterLogin';
import { bulkDeleteProjects } from '../../../../redux/Project/saga';

interface ProjectGridProps {
  projects: Project[];
  onProjectClick: (project: Project) => void;
  setShowBackNavigation: (show: boolean) => void;
  isProjectsLoading?: boolean;
  hasActiveFilters?: boolean;
  originalProjects?: Project[];
  fetchProjectList: () => void;
}

const formatDate = (dateString: string) => {
  if(!dateString || dateString === 'Invalid Date') return '';
  const date = new Date(dateString);
  if(date.toString() === 'Invalid Date') return '';
  const day = date.getDate().toString().padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
};

export const ProjectGrid: React.FC<ProjectGridProps> = ({
  projects,
  onProjectClick,
  setShowBackNavigation,
  isProjectsLoading = false,
  hasActiveFilters = false,
  originalProjects,
  fetchProjectList,
}) => {
  const dispatch = useDispatch();
  const { hasPermission } = useUserPermissionsAfterLogin();
  const [selectedProjects, setSelectedProjects] = useState<number[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  // Check if user has permission to manage admins (superadmin only)
  const canManageProjects = hasPermission('manage_admins');

  const {
    displayedProjects,
    hasMore,
    isLoading,
    loadMore,
  } = useProjectPagination({ projects });

  const handleProjectSelect = (projectId: number) => {
    setSelectedProjects(prev => 
      prev.includes(projectId) 
        ? prev.filter(id => id !== projectId)
        : [...prev, projectId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProjects.length === displayedProjects.length) {
      setSelectedProjects([]);
    } else {
      setSelectedProjects(displayedProjects.map(project => Number(project.projectId)));
    }
  };

  const handleBulkDelete = () => {
    if (selectedProjects.length === 0) return;
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async (softDelete: boolean) => {
    if (selectedProjects.length === 0) return;
    
    setIsDeleting(true);
    const payload = {
      projectIds: selectedProjects,
      softDelete: softDelete
    }
  //  console.log('payload', payload);
    try {
      //dispatch(bulkDeleteProjects(payload));
      const response = await bulkDeleteProjects(payload);
    //  console.log('response', response);
      if(response){
        setSelectedProjects([]);
        setShowDeleteModal(false);
        fetchProjectList();
      }
     
    } catch (error) {
      console.error('Error deleting projects:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
  };

  // Get selected project objects for the modal
  const selectedProjectObjects = displayedProjects.filter(project => 
    selectedProjects.includes(Number(project.projectId))
  );
  // Check if there are any projects to display
  const hasAnyProjects = displayedProjects.length > 0;
  const hasOriginalProjects = originalProjects && originalProjects.length > 0;
  const hasProjects = projects.length > 0;
  
 // console.log('hasAnyProjects:', hasAnyProjects, 'hasOriginalProjects:', hasOriginalProjects, 'hasProjects:', hasProjects);
 // console.log('hasActiveFilters:', hasActiveFilters);

  // Show loader first when loading projects
  if (isProjectsLoading) {
    return (
      <div className="flex items-center justify-center min-h-[700px]">
        <div className="flex items-center space-x-3">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#EB1C37]"></div>
          <span className="text-gray-600 text-lg">Loading projects data...</span>
        </div>
      </div>
    );
  }

  // Show empty state when no projects are found (only after loading is complete)
  if (!hasAnyProjects) {
    return (
      <div className="flex flex-col items-center justify-center h-full mt-10">
        <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mb-6 shadow-sm border border-gray-100">
          <Package className="h-10 w-10 text-gray-400" />
        </div>
        <h3 className="text-xl font-semibold text-gray-900 mb-3 leading-tight">
          {hasActiveFilters ? 'No Projects Match Your Filters' : 'No Projects Found'}
        </h3>
        <p className="text-gray-500 text-sm leading-relaxed">
          {hasActiveFilters 
            ? 'Try adjusting your filters to see more projects.' 
            : 'Please add a new project to get started.'
          }
        </p>
      </div>
    );
  }

  return (
    <>
      {/* Bulk Actions Bar - Only visible to superadmin */}
          {canManageProjects && selectedProjects.length > 0 && (
            <div className="mb-4 p-4 bg-red-50 border rounded-lg flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-sm font-medium text-red-800">
                  {selectedProjects.length} project(s) selected
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedProjects([])}
                  className="text-red-600 hover:bg-red-100"
                >
                  Clear Selection
                </Button>
              </div>
              <Button
            //    variant="destructive"
                size="sm"
                onClick={handleBulkDelete}
                disabled={isDeleting}
                className="flex items-center space-x-2"
              >
                <Trash2 className="h-4 w-4" />
                <span>{isDeleting ? 'Deleting...' : 'Delete Selected'}</span>
              </Button>
            </div>
          )}

          {/* Select All Checkbox - Only visible to superadmin */}
          {canManageProjects && (
            <div className="mb-4 flex items-center space-x-2">
              <Checkbox
                id="select-all"
                checked={selectedProjects.length === displayedProjects.length && displayedProjects.length > 0}
                onCheckedChange={handleSelectAll}
              />
              <label htmlFor="select-all" className="text-sm font-medium text-gray-700">
                Select All Projects
              </label>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-4">
            {displayedProjects.map(project => (
          <div
            key={project.projectId}
            onClick={(e) => {
              // Don't trigger project click if clicking on checkbox
              if ((e.target as HTMLElement).closest('[role="checkbox"]')) return;
              onProjectClick(project);
              setShowBackNavigation(true);
            }}
            className="bg-gradient-to-br from-white to-gray-50/50 rounded-lg px-4 py-3 shadow-sm hover:shadow-lg transition-all duration-300 cursor-pointer border border-gray-200/60 hover:border-gray-300/80 backdrop-blur-sm relative"
          >
            <div className="flex items-start gap-3 mb-2">
              {canManageProjects && (
                <div className="flex-shrink-0 mt-1">
                  <Checkbox
                    id={`select-project-${project.projectId}`}
                    checked={selectedProjects.includes(Number(project.projectId))}
                    onCheckedChange={(checked) => {
                      handleProjectSelect(Number(project.projectId));
                    }}
                  />
                </div>
              )}
              
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-start">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-semibold text-[#4a6da7] mb-1" title={project.projectName}>
                      {project.projectName.length > 20 
                        ? `${project.projectName.substring(0, 20)}...` 
                        : project.projectName}
                    </h3>
                    <p className="text-gray-500 text-sm">#{project.projectNumber}</p>
                  </div>
                  <StatusBadge status={project.progress} className={STATUS_COLORS[project.progress as keyof typeof STATUS_COLORS] || STATUS_COLORS.default} />
                </div>
              </div>
            </div>
            <div className="space-y-2 mt-3">
              <div className="flex items-center gap-2 text-gray-600">
                <MapPin className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{project.location}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Building2 className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{project.customer}</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Calendar className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <span className="text-sm">{formatDate(project.startDate)} to {formatDate(project.endDate)}</span>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-1">
              {project.projectTypes.pipeline?.enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-md text-xs font-medium flex-shrink-0">
                  <Cable className="h-3 w-3" />
                  Pipeline
                </span>
              )}
              {project.projectTypes.fabrication?.enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-blueprint-blue bg-[rgba(0,39,63,0.06)] rounded-md text-xs font-medium flex-shrink-0">
                  <Wrench className="h-3 w-3" />
                  Fabrication
                </span>
              )}
              {project.projectTypes.others?.enabled && (
                <span className="inline-flex items-center gap-1 px-2 py-1 text-galvanizing-green bg-[rgba(31,154,58,0.08)] rounded-md text-xs font-medium flex-shrink-0">
                  <Activity className="h-3 w-3" />
                  Others
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {hasMore && (
        <div className="sticky bottom-0 z-10 flex justify-center pt-6 pb-4 mt-4 bg-gradient-to-t from-page-bg via-page-bg to-transparent">
          <Button
            onClick={loadMore}
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="h-5 w-5 animate-spin" />
            ) : (
              <Plus className="h-5 w-5" />
            )}
            Load More Projects
          </Button>
        </div>
      )}

      {/* Project Delete Confirmation Modal - Only accessible to superadmin */}
      {canManageProjects && (
        <ProjectDeleteModal
          isOpen={showDeleteModal}
          onClose={handleCloseDeleteModal}
          projects={selectedProjectObjects}
          onConfirmDelete={handleConfirmDelete}
          isDeleting={isDeleting}
        />
      )}
    </>
  );
}; 